/**
 * Leaf schema helpers for the BFF seam (`bff-schemas.ts`) — branded/enum
 * wire-value primitives, split out (W3.2, auth-debt epic) so the seam file
 * stays under the max-lines ceiling as the contract grows. Pure zod, plus a
 * TYPE-ONLY import of the branded SSOT (`ExactInt`) — which sits at the bottom
 * of the web DAG and compiles to nothing — so nothing here can cycle back into
 * the seam.
 */
import type { ExactInt } from '@letsgethome/types/branded';
import * as z from 'zod/mini';

/**
 * A branded wire string (`Brand<string, …>`): runtime = "is a string". The
 * brand is a compile-time-only refinement of `string`, so `z.custom<B>` with a
 * string check claims nothing the runtime doesn't verify — it is the
 * schema-side (cast-free) mirror of the sanctioned branded-constructor
 * crossings in `@letsgethome/types/branded`.
 */
export function brandedString<B extends string>(): z.ZodMiniType<B> {
  return z.custom<B>((value) => typeof value === 'string');
}

/** A branded wire number (`Brand<number, …>`): runtime = "is a number". */
export function brandedNumber<B extends number>(): z.ZodMiniType<B> {
  return z.custom<B>((value) => typeof value === 'number');
}

/**
 * An `ExactInt` on the wire: a decimal-integer LITERAL (`'1500000'`).
 *
 * The brand's only mint is `exactInt(bigint)`, whose output is exactly
 * `bigint.toString()` — so `/^-?\d+$/` is not a heuristic, it is that mint's
 * RANGE stated as a predicate. A value that passes really can be read back by
 * `exactIntValue` without a `try`, which is the whole promise of the brand.
 *
 * It REFUSES a number, and that strictness is the point. It replaced
 * `brandedCoercedBigInt` (DELETED in R4-2), which coerced `1500000` and
 * `'1500000'` alike into a `bigint`: the coercion made the SERVER tolerant of
 * a body the BROWSER could not have sent, because the contract type feeding
 * the browser's `JSON.stringify` was the `bigint` the encoder throws on. One
 * carrier, one spelling, on both sides of the seam — and the
 * `BffPostSchemaParityGate` is what holds this schema and that contract type
 * equal, so the strictness is licensed rather than merely chosen.
 */
export function exactIntSchema(): z.ZodMiniType<ExactInt> {
  return z.custom<ExactInt>((value) => typeof value === 'string' && /^-?\d+$/.test(value));
}

/**
 * The NAME union of a generated proto TS enum (`keyof typeof Enum` — the
 * view-model enum pattern, e.g. `'ADDRESS' | 'SIZE' | …`). Derived from the
 * enum OBJECT at runtime (numeric reverse-mapping keys filtered out), so the
 * accepted vocabulary and the proto enum can't drift.
 */
export function protoEnumNameSchema<E extends Record<string, string | number>>(
  enumObject: E
): z.ZodMiniType<keyof E & string> {
  const names = Object.keys(enumObject).filter((key) => Number.isNaN(Number(key)));
  return z.enum(names) as z.ZodMiniType<keyof E & string>;
}

/**
 * {@link protoEnumNameSchema} minus the `UNSPECIFIED` zero — for a view-model
 * field whose absence is spelled by the field being ABSENT, so the zero never
 * reaches it (D3's `suspensionReason`).
 */
export function statedProtoEnumNameSchema<E extends Record<string, string | number>>(
  enumObject: E
) {
  // The zero variant is the one whose VALUE is 0 — read off the enum object,
  // never spelled, so a renamed zero cannot slip through.
  const isStated = (key: string): key is Exclude<keyof E & string, 'UNSPECIFIED'> =>
    Number.isNaN(Number(key)) && enumObject[key] !== 0;
  return z.enum(Object.keys(enumObject).filter(isStated));
}

/**
 * The VALUE union of a generated proto TS enum (numeric wire tags, e.g.
 * `UserProfile.roles`): accepts exactly the enum's numeric values, DERIVED from
 * the enum object at runtime (reverse-mapping name strings filtered out). Typed
 * via the explicit `V` (the declared enum type) rather than `z.enum(Enum)`'s
 * member-literal union, which TS does not consider identical to `V`.
 */
export function protoEnumValueSchema<V extends number>(
  enumObject: Record<string, string | number>
): z.ZodMiniType<V> {
  const values = new Set(Object.values(enumObject).filter((value) => typeof value === 'number'));
  return z.custom<V>((value) => typeof value === 'number' && values.has(value));
}
